import React, { useState, useContext } from "react";
import { useTheme } from "@emotion/react";
import { Box, useMediaQuery } from "@mui/material";
import { useIsAuthenticated } from "@azure/msal-react";
import { useWindowSize } from "usehooks-ts";
import { Context } from "../context/context";
import LoginPage from "./login-page";
import MainFooter from "./main-footer";
import ChatArea from "./chat-area";
import DocumentArea from "./document-area";
import Sidebar from "../components/sidebar";
import config from "../config.json";

const Main = () => {
    const { previewDocumentContext } = useContext(Context);
    const [previewDocument] = previewDocumentContext;

    const enableMSAL = !!config.MSAL?.auth?.clientId;
    const isAuthenticated = useIsAuthenticated();

    const { height = 0 } = useWindowSize();
    const theme = useTheme();

    const showPreview = previewDocument;
    const smallDevice = useMediaQuery(theme.breakpoints.down("lg"));
    const tinyDevice = useMediaQuery(theme.breakpoints.down("sm"));
    const showChat = !showPreview || (showPreview && !smallDevice);
    const showFooter = config.FOOTER && showChat && !tinyDevice;

    // Set widths based on preview state
    const chatWidth = showPreview ? (smallDevice ? "100%" : "50%") : "100%";
    const previewWidth = showPreview ? (smallDevice ? "100%" : "50%") : "0%";

    // State to manage which panel is active
    const [activePanel, setActivePanel] = useState(null);

    // Overall outer container styles
    const outerBoxStyles = {
        display: "flex",
        justifyContent: "flex-start",
        height: height && height > 0 ? height + "px" : "100vh",
        backgroundColor: "background.outer",
        width: "100%",
    };

    // Inner container to center content (applies margin when preview is not shown)
    const innerContainerStyles = {
        display: "flex",
        flexDirection: "row",
        width: showPreview ? "100%" : "70%",
        marginLeft: "0",
        marginRight: showPreview ? "0" : "30%",
        transition: "all 0.3s ease",
    };

    // Main container styles for chat/document and footer
    const mainContainerStyles = {
        borderRadius: "10px",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.default",
        padding: "10px",
        height: "100%",
        width: "100%",
    };

    // If MSAL is enabled and the user is not authenticated, show only the login page.
    if (enableMSAL && !isAuthenticated) {
        return (
            <Box sx={outerBoxStyles}>
                <Box sx={innerContainerStyles}>
                    <Box id="main-container" sx={mainContainerStyles}>
                        <LoginPage />
                    </Box>
                </Box>
            </Box>
        );
    }

    // When authenticated, conditionally render the sidebar:
    return (
        <Box sx={outerBoxStyles}>
            <Box sx={innerContainerStyles}>
                {/* Render Sidebar only if showPreview is false */}
                {!showPreview && (
                    <Sidebar activePanel={activePanel} setActivePanel={setActivePanel} />
                )}

                {/* Main Container */}
                <Box id="main-container" sx={mainContainerStyles}>
                    <div
                        id="content-container"
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            height: "100%",
                            minHeight: 0,
                        }}
                    >
                        {/* Chat Area */}
                        {showChat && (
                            <div
                                id="chat-container"
                                style={{
                                    height: "100%",
                                    width: chatWidth,
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <ChatArea />
                            </div>
                        )}

                        {/* Document Area */}
                        {showPreview && (
                            <div
                                id="preview-container"
                                style={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    width: previewWidth,
                                }}
                            >
                                <DocumentArea />
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {showFooter && showChat && (
                        <div id="main-footer" style={{ padding: "10px" }}>
                            <MainFooter />
                        </div>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default Main;
